import { Component, OnInit, Inject, DOCUMENT } from '@angular/core'

import { Subscription, timer } from 'rxjs'
import { ErrorMessage, UiService } from 'src/app/services/ui.service'
import { subscriptionActive } from 'src/utils/rxjs-utils'
import { datadogRum } from '@datadog/browser-rum'
import { NavigationEnd, Router } from '@angular/router'
import { LoggerService } from 'src/app/account/services/logger.service'

@Component({
  selector: 'app-root',
  templateUrl: './ui-shell.component.html',
  styleUrls: ['./ui-shell.component.scss'],
  standalone: false,
})
export class UiShell implements OnInit {
  errorMessage: string = null
  private hideErrorTimerSubscription: Subscription = null
  private fadeErrorTimerSubscription: Subscription = null

  constructor(
    private uiService: UiService,
    private router: Router,
    private logger: LoggerService,
    @Inject(DOCUMENT) private document: Document
  ) {
    this.router.events.subscribe((event) => {
      if (event instanceof NavigationEnd) {
        datadogRum.startView(event.urlAfterRedirects) // Track route changes
        this.logger.info(`User navigated to: ${event.urlAfterRedirects}`)
      }
    })
  }

  ngOnInit() {
    // Initialize font loading optimization
    this.initializeFontLoading()

    this.uiService.errorStream.subscribe((value: ErrorMessage) => {
      if (subscriptionActive(this.hideErrorTimerSubscription)) {
        this.hideErrorTimerSubscription.unsubscribe()
      }
      if (subscriptionActive(this.fadeErrorTimerSubscription)) {
        this.fadeErrorTimerSubscription.unsubscribe()
      }

      console.error(value.detailedMessage)
      this.errorMessage = value.detailedMessage

      // set a timer to dismiss the error message.
      this.hideErrorTimerSubscription = timer(5000).subscribe((_) => {
        // set a timer to clear the error message after the fade completes.
        // this timing has to be matched to the timing of the animation in
        // the css!
        this.fadeErrorTimerSubscription = timer(500).subscribe((_) => {
          this.errorMessage = null
        })
      })
    })
  }

  private initializeFontLoading() {
    // Add fonts-loading class initially
    this.document.body.classList.add('fonts-loading')

    // Modern approach: Use Font Loading API
    if (this.document.fonts) {
      // Check if fonts are already loaded
      this.document.fonts.ready.then(() => {
        this.checkSpecificFonts()
      })
    } else {
      // Fallback for very old browsers
      this.fallbackFontCheck()
    }
  }

  private checkSpecificFonts() {
    try {
      // Check if Source Sans 3 is available using FontFace.load()
      this.document.fonts
        .load('12px "Source Sans 3"')
        .then(() => {
          this.onFontsLoaded()
        })
        .catch(() => {
          // Font failed to load, use fallback timing
          this.fallbackFontCheck()
        })
    } catch (error) {
      // FontFace.load not supported, use fallback
      this.fallbackFontCheck()
    }
  }

  private fallbackFontCheck() {
    // Simple timeout for older browsers - most fonts load within 2-3 seconds
    setTimeout(() => {
      this.onFontsLoaded()
    }, 3000)
  }

  private onFontsLoaded() {
    this.document.body.classList.remove('fonts-loading')
    this.document.body.classList.add('fonts-loaded')
  }

  get showError() {
    return !!this.errorMessage && !subscriptionActive(this.fadeErrorTimerSubscription)
  }

  get hideError() {
    return !!this.errorMessage && subscriptionActive(this.fadeErrorTimerSubscription)
  }
}
